import React, { createContext, FC, PropsWithChildren, useReducer } from 'react';
import { Account, PatchAccountDto } from '@packages/models/api';
import { AuthService } from './auth.service';
import { MobileStorageKeys, StorageService } from '@packages/core/storage';

import { HttpClient } from '@packages/core/http';
import { getLogger } from '@packages/core/logging';

import { authActions, AuthActionDispatcher, authReducer, AuthState, AuthStatus, initialState } from './auth.state';
import { QmrsService, RetailerSearchParams } from '../qmrs';
import { AuthTokenService, StoredToken } from './auth-token.service';

const logger = getLogger('auth');

export const AuthStateContext = createContext<AuthState | undefined>(undefined);
export const AuthDispatchContext = createContext<AuthActionDispatcher | undefined>(undefined);

export const AuthProvider: FC<PropsWithChildren> = ({ children }) => {
    const [state, dispatch] = useReducer(authReducer, initialState);

    return (
        <AuthStateContext.Provider value={state}>
            <AuthDispatchContext.Provider value={dispatch}>{children}</AuthDispatchContext.Provider>
        </AuthStateContext.Provider>
    );
};

interface AuthDataAccess {
    authService: AuthService;
    authDispatch: AuthActionDispatcher;
    qmrsService?: QmrsService;
    authTokenService: AuthTokenService;
    storageService?: StorageService;
    signal?: AbortSignal;
    ignoreCache?: boolean;
}

export async function fetchAuthUserProfile({
    accountId,
    currentAuthStatus,
    authService,
    authDispatch,
    signal,
    account,
}: {
    accountId: string;
    currentAuthStatus: AuthStatus;
    /**
     * Account resolved from the access token's `account` claim. When present it is
     * used directly, sparing a redundant `GET /accounts/{id}` round-trip. Absent for
     * tokens issued before the capabilities-in-JWT rollout, where we fall back to the API.
     */
    account?: Account | null;
} & Omit<AuthDataAccess, 'qmrsService' | 'authTokenService'>) {
    if (currentAuthStatus !== AuthStatus.Authenticated) {
        const status = AuthStatus.FetchingProfile;
        authDispatch(authActions.setAuthStatus({ status }));
    }

    if (account) {
        authDispatch(authActions.setAccount({ account }));
        return;
    }

    const response = await authService.getAccount(accountId, signal);

    if (!response.success && response.aborted) {
        return;
    } else if (!response.success) {
        return Promise.reject(response.data);
    }

    authDispatch(authActions.setAccount({ account: response.data.account }));

    return response;
}

export async function patchAuthUserProfile({
    authDispatch,
    authService,
    accountId,
    accountPatch,
    signal,
    httpClient,
}: {
    accountId: string;
    accountPatch: PatchAccountDto;
    /**
     * When provided, the access token is refreshed after a successful patch. The token
     * embeds a snapshot of the account, so without this a reload (`checkAuth`) or auto-refresh
     * would rehydrate stale profile data. `refresh-access-token` rebuilds the claim from the
     * DB, so the regenerated token reflects the patch. Best-effort — a refresh failure does not
     * fail the patch, since `state.account` is already updated from the response.
     */
    httpClient?: HttpClient;
} & Omit<AuthDataAccess, 'qmrsService' | 'authTokenService'>) {
    const response = await authService.patchAccount(accountId, accountPatch, signal);

    if (!response.success && response.aborted) {
        return;
    } else if (!response.success) {
        return Promise.reject(response.data);
    }

    authDispatch(authActions.setAccount({ account: response.data.account }));

    if (httpClient) {
        try {
            await httpClient.forceRefreshToken();
        } catch (e) {
            logger.warn('Failed to refresh the access token after the profile update', { error: e });
        }
    }

    return response;
}

export async function fetchAuthUserRetailers({
    searchParams,
    qmrsService,
    authDispatch,
    signal,
    ignoreCache,
}: { searchParams: RetailerSearchParams } & Omit<AuthDataAccess, 'authService' | 'authTokenService'>) {
    if (!qmrsService) {
        return Promise.reject('Invalid arugments. qmrsService is required to fetch the authUser retailers');
    }

    const response = await qmrsService.fetchRetailers({
        searchParams,
        signal,
        ignoreCache,
    });

    if (!response.success && response.aborted) {
        return;
    } else if (!response.success) {
        return Promise.reject(response.data);
    }

    authDispatch(authActions.setRetailers({ retailers: response.data.retailers }));

    return response;
}

export async function setToken({
    token,
    authService,
    authTokenService,
    storageService,
    authDispatch,
    signal,
}: { token: string } & AuthDataAccess) {
    let status = AuthStatus.Saving;
    authDispatch(authActions.setAuthStatus({ status }));
    let storedToken: StoredToken | null;
    try {
        storedToken = await authTokenService.storeToken(token);
        await storageService!.setItem(MobileStorageKeys.AuthTokenTimestamp, Date.now().toString());
    } catch (e) {
        alert(e);
        logger.error('Failed to store the access token', { error: e });
        throw e;
    }

    if (!storedToken || !storedToken.decodedToken) {
        return;
    }

    await fetchAuthUserProfile({
        accountId: storedToken.decodedToken.sub,
        currentAuthStatus: status,
        authService,
        authDispatch,
        signal,
        account: storedToken.account,
    });
}

export const AUTH_SESSION_TIMEOUT_MS = 8 * 60 * 60 * 1000;

export async function getAuthSessionRemainingMs({ storageService }: Pick<AuthDataAccess, 'storageService'>) {
    const storedTokenTimestampString = await storageService!.getItem(MobileStorageKeys.AuthTokenTimestamp);
    const storedTokenTimestamp = storedTokenTimestampString ? parseInt(storedTokenTimestampString, 10) : NaN;
    if (!Number.isFinite(storedTokenTimestamp)) {
        return null;
    }
    return AUTH_SESSION_TIMEOUT_MS - (Date.now() - storedTokenTimestamp);
}

export async function touchAuthSession({ storageService }: Pick<AuthDataAccess, 'storageService'>) {
    await storageService!.setItem(MobileStorageKeys.AuthTokenTimestamp, Date.now().toString());
}

export async function renewAuthSession({
    storageService,
    refreshAccessToken,
}: Pick<AuthDataAccess, 'storageService'> & { refreshAccessToken: () => Promise<unknown> }) {
    await refreshAccessToken();
    await touchAuthSession({ storageService });
}

export async function checkAuth({ authService, authTokenService, storageService, authDispatch }: AuthDataAccess) {
    let status = AuthStatus.Checking;
    authDispatch(authActions.setAuthStatus({ status }));

    try {
        const remainingMs = await getAuthSessionRemainingMs({ storageService });
        if (remainingMs == null || remainingMs < 0) {
            return null;
        }

        const storedToken = await authTokenService.getStoredToken();
        if (!storedToken || !storedToken.decodedToken) {
            return null;
        }

        await fetchAuthUserProfile({
            accountId: storedToken.decodedToken.sub,
            currentAuthStatus: status,
            authService,
            authDispatch,
            account: storedToken.account,
        });

        const token = storedToken.token;
        if (!token) {
            return null;
        }

        authDispatch(authActions.setAuthStatus({ status: AuthStatus.Authenticated, token }));
        return token;
    } catch (e) {
        logger.error('Failed to check the stored authentication state', { error: e });
        throw e;
    }
}
